/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useState } from 'react';
import Space from '@/components/Space';
import FieldNameWrapper from '../../../utils/FieldName/FieldNameWrapper';
import StyledFieldName from '../../../utils/FieldName/StyledFieldName';
import { FormattedMessage } from 'react-intl';
import TextEditor from '../../../utils/TextEditor';
import type { KeyedExternalFile, KeyedImage } from './NewLogEntryFiles';
import useComplexState from '@/lib/utils/useComplexState';
import NewLogEntryFiles from './NewLogEntryFiles';

type NewLogEntryFormProps = {
  logEntryName: string;
  onSave: (
    value: string | null,
    attachedImages: Map<string, KeyedImage>,
    attachedExternalFiles: Map<string, KeyedExternalFile>,
  ) => Promise<void>;
  onCancel: () => void;
};

const NewLogEntryForm = ({
  logEntryName,
  onSave,
  onCancel,
}: NewLogEntryFormProps) => {
  const [saving, setSaving] = useState(false);
  const [attachedImages, setAttachedImages] = useComplexState<
    Map<string, KeyedImage>
  >(new Map());
  const [attachedExternalFiles, setAttachedExternalFiles] = useComplexState<
    Map<string, KeyedExternalFile>
  >(new Map());

  const handleAddImages = (images: KeyedImage[]): void => {
    setAttachedImages(attachedImages => {
      for (const image of images) {
        attachedImages.set(image.key, image);
      }
    });
  };

  const handleRemoveImage = (image: KeyedImage): void => {
    setAttachedImages(attachedImages => {
      attachedImages.delete(image.key);
    });
  };

  const handleAddExternalFiles = (externalFiles: KeyedExternalFile[]): void => {
    setAttachedExternalFiles(attachedExternalFiles => {
      for (const externalFile of externalFiles) {
        attachedExternalFiles.set(externalFile.key, externalFile);
      }
    });
  };

  const handleRemoveExternalFile = (externalFile: KeyedExternalFile): void => {
    setAttachedExternalFiles(attachedExternalFiles => {
      attachedExternalFiles.delete(externalFile.key);
    });
  };

  const handleSave = useCallback(
    async (value: string | null): Promise<void> => {
      const hasAttachedImages = attachedImages.size > 0;
      const hasAttachedExternalFiles = attachedExternalFiles.size > 0;

      if (value === null && !hasAttachedImages && !hasAttachedExternalFiles) {
        onCancel();

        return;
      }

      setSaving(true);

      await onSave(value, attachedImages, attachedExternalFiles);

      setSaving(false);
    },
    [attachedImages, attachedExternalFiles],
  );

  return (
    <section>
      <Space height={18} />
      <FieldNameWrapper>
        <StyledFieldName>
          <FormattedMessage
            id="portfolio.challenge.log_entries.add.title"
            values={{ logEntryName }}
          />
        </StyledFieldName>
      </FieldNameWrapper>
      <TextEditor onSave={handleSave} disabled={saving} />
      <NewLogEntryFiles
        saving={saving}
        images={Array.from(attachedImages.values())}
        onAddImages={handleAddImages}
        onRemoveImage={handleRemoveImage}
        externalFiles={Array.from(attachedExternalFiles.values())}
        onAddExternalFiles={handleAddExternalFiles}
        onRemoveExternalFile={handleRemoveExternalFile}
      />
    </section>
  );
};

export default NewLogEntryForm;
